// Shared FAQ surfaces — the slide-up panel of saved questions and the Pinned
// chips bar that sits above the composer. Rendered by BOTH chat surfaces: the
// dashboard page and the embeddable widget, which reach the same per-user store
// (`/chat/faq`) from their own auth (see docs/faq.md).
//
// Presentational: the host owns fetching and mutating (react-query in the SPA,
// a plain hook in the widget) and passes the rows plus the four callbacks.
// Hosts load the matching styles (`@netwatchai/chat-core/faq.css`; the widget
// inlines it into its shadow DOM).

import { useMemo, useState } from 'react'
import type { Ref } from 'react'

/** One saved question — the wire shape of `/chat/faq`. */
export interface FaqEntry {
  id: string
  category: string
  question: string
  starred: boolean
  starred_at: string | null
  created_at: string
}

/** Starred rows, oldest star first — chip positions stay stable as new stars
 *  append. Numeric compare: ISO strings with and without a fractional-seconds
 *  part don't sort lexically. */
export function pinnedEntries(entries: FaqEntry[] | null | undefined): FaqEntry[] {
  const ts = (e: FaqEntry) => (e.starred_at ? Date.parse(e.starred_at) : 0)
  return (entries ?? []).filter((e) => e.starred).sort((a, b) => ts(a) - ts(b))
}

/** Category buckets in first-appearance order (rows arrive created_at-asc, so
 *  seeded categories keep their historic order); uncategorized sits last. */
function groupByCategory(entries: FaqEntry[]): [string, FaqEntry[]][] {
  const buckets = new Map<string, FaqEntry[]>()
  let uncategorized: FaqEntry[] | null = null
  for (const e of entries) {
    if (!e.category) {
      uncategorized = uncategorized ?? []
      uncategorized.push(e)
      continue
    }
    const bucket = buckets.get(e.category) ?? []
    bucket.push(e)
    buckets.set(e.category, bucket)
  }
  const out = [...buckets.entries()]
  if (uncategorized) out.push(['', uncategorized])
  return out
}

// Small stroke star for the per-row toggle (filled via CSS when on).
function Star() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l2.7 5.6 6.1.8-4.5 4.3 1.1 6L12 16.8l-5.4 2.9 1.1-6-4.5-4.3 6.1-.8z" />
    </svg>
  )
}

function Cross() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  )
}

function Sparkle() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.6 4.6L18 9l-4.4 1.4L12 15l-1.6-4.6L6 9l4.4-1.4z" />
    </svg>
  )
}

function QuestionMark() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.2a2.5 2.5 0 1 1 3.6 2.3c-.8.4-1.1.9-1.1 1.8M12 17h.01" />
    </svg>
  )
}

export interface FaqPanelProps {
  open: boolean
  /** The panel's root element — the host's press-outside-to-close handler needs
   *  it to tell inside from outside. */
  rootRef?: Ref<HTMLDivElement>
  /** null = still loading (or failed / unavailable, see the flags below). */
  entries: FaqEntry[] | null
  loadFailed?: boolean
  /** The server refused this session permanently — say so instead of offering a
   *  retry that can never succeed. */
  unavailable?: boolean
  /** Copy for that permanent refusal (hosts differ in what the fix is). */
  unavailableText?: string
  onRetry?: () => void
  /** Server-enforced pin cap, echoed by `GET /chat/faq`. */
  starCap: number
  pinnedCount: number
  /** A reply is streaming — rows can't be asked. */
  chatBusy?: boolean
  /** A mutation is in flight — the controls hold still. */
  mutating?: boolean
  /** Last mutation error; the backend writes user-facing details. */
  error?: string | null
  onAsk: (question: string) => void
  onAdd: (question: string, category: string, onDone: () => void) => void
  onStar: (entry: FaqEntry) => void
  onDelete: (entry: FaqEntry) => void
}

/**
 * The slide-up FAQ panel: category-grouped saved questions with pin/delete
 * controls and an add form. Owns only the draft inputs — hosts remount it
 * (`key={owner}`) to reset them when the signed-in user changes.
 */
export function FaqPanel({
  open,
  rootRef,
  entries,
  loadFailed = false,
  unavailable = false,
  unavailableText = 'Saved questions aren’t available for this session.',
  onRetry,
  starCap,
  pinnedCount,
  chatBusy = false,
  mutating = false,
  error,
  onAsk,
  onAdd,
  onStar,
  onDelete,
}: FaqPanelProps) {
  const [newQ, setNewQ] = useState('')
  const [newCat, setNewCat] = useState('')
  // Native <datalist> popups take the browser's own colors and ignore the page
  // theme, so category suggestions render as our own themed popover instead.
  const [catOpen, setCatOpen] = useState(false)

  // Existing category names for the suggestions popover, filtered by the
  // current draft (and hidden once the draft matches one exactly).
  const catOptions = useMemo(() => {
    const all = [...new Set((entries ?? []).map((e) => e.category).filter(Boolean))]
    const t = newCat.trim().toLowerCase()
    if (!t) return all
    return all.filter((c) => c.toLowerCase().includes(t) && c.toLowerCase() !== t)
  }, [entries, newCat])

  const grouped = useMemo(() => groupByCategory(entries ?? []), [entries])
  const formDisabled = mutating || entries === null

  return (
    <div ref={rootRef} className={open ? 'nw-faq nw-faq-open' : 'nw-faq'}>
      <form
        className="nw-faq-add"
        onSubmit={(e) => {
          e.preventDefault()
          const q = newQ.trim()
          if (!q || formDisabled) return
          onAdd(q, newCat.trim(), () => setNewQ(''))
        }}
      >
        <input
          type="text"
          value={newQ}
          placeholder="Add your own question…"
          disabled={formDisabled}
          onChange={(e) => setNewQ(e.target.value)}
        />
        <div className="nw-faq-catwrap">
          <input
            type="text"
            className="nw-faq-cat-in"
            value={newCat}
            placeholder="Category"
            disabled={formDisabled}
            onChange={(e) => {
              setNewCat(e.target.value)
              setCatOpen(true)
            }}
            onFocus={() => setCatOpen(true)}
            onBlur={() => setCatOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setCatOpen(false)
            }}
          />
          {catOpen && catOptions.length > 0 && (
            <div className="nw-faq-catmenu">
              {catOptions.map((c) => (
                <button
                  type="button"
                  key={c}
                  className="nw-faq-catopt"
                  // mousedown fires before the input's blur — keep focus so the
                  // click lands before the menu unmounts
                  onMouseDown={(ev) => ev.preventDefault()}
                  onClick={() => {
                    setNewCat(c)
                    setCatOpen(false)
                  }}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
        <button type="submit" disabled={formDisabled || !newQ.trim()}>Add</button>
      </form>

      {error && <div className="nw-faq-err">{error}</div>}

      {grouped.map(([cat, rows]) => (
        <div key={cat || '__mine'}>
          <div className="nw-faq-cat">{cat || 'My questions'}</div>
          {rows.map((e) => (
            <div className="nw-faq-row" key={e.id}>
              <button className="nw-faq-q" disabled={chatBusy} onClick={() => onAsk(e.question)}>
                {e.question}
              </button>
              <button
                className={e.starred ? 'nw-faq-star on' : 'nw-faq-star'}
                disabled={mutating || (!e.starred && pinnedCount >= starCap)}
                title={
                  e.starred
                    ? 'Unpin'
                    : pinnedCount >= starCap
                      ? `Pin limit (${starCap}) reached — unpin something first`
                      : 'Pin to the bar below'
                }
                aria-label={e.starred ? 'Unpin question' : 'Pin question'}
                onClick={() => onStar(e)}
              >
                <Star />
              </button>
              <button
                className="nw-faq-del"
                disabled={mutating}
                title="Delete"
                aria-label="Delete question"
                onClick={() => onDelete(e)}
              >
                <Cross />
              </button>
            </div>
          ))}
        </div>
      ))}

      {entries === null &&
        (unavailable ? (
          <div className="nw-faq-cat">{unavailableText}</div>
        ) : loadFailed ? (
          <div className="nw-faq-cat">
            Couldn’t load your questions —{' '}
            <button type="button" className="nw-faq-retry" onClick={onRetry}>retry</button>
          </div>
        ) : (
          <div className="nw-faq-cat">Loading…</div>
        ))}
    </div>
  )
}

export interface FaqBarProps {
  /** The starred rows, already ordered — use `pinnedEntries()`. */
  pinned: FaqEntry[]
  /** Toggle button element, so the host can exclude it from its
   *  press-outside-to-close handler (otherwise the outside-press closes and the
   *  click immediately reopens). */
  buttonRef?: Ref<HTMLButtonElement>
  onToggle: () => void
  onAsk: (question: string) => void
  busy?: boolean
}

/** The Pinned chips row: one chip per starred question, plus the FAQ toggle. */
export function FaqBar({ pinned, buttonRef, onToggle, onAsk, busy = false }: FaqBarProps) {
  return (
    <div className="nw-faq-bar">
      <span className="nw-faq-barlabel"><Sparkle /> Pinned</span>
      <div className="nw-faq-chips">
        {pinned.map((e) => (
          <button className="nw-faq-chip" key={e.id} disabled={busy} onClick={() => onAsk(e.question)}>
            {e.question}
          </button>
        ))}
      </div>
      <button ref={buttonRef} className="nw-faq-btn" onClick={onToggle}>
        <QuestionMark /> FAQ
      </button>
    </div>
  )
}
